import { useCallback, useEffect, useMemo, useState } from 'react';
import { FolderKanban, MoreHorizontal, Plus } from 'lucide-react';
import { toast } from 'sonner';
import type {
  Project,
  ProjectListItem,
  ProjectTagRuleApplyResult,
  UpdateProjectDto,
} from '@ip/shared-types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { HeaderActions } from '@/components/layout/AppShell';
import { useProjectsApi } from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import { hasCompanyRole } from '@/lib/permissions';
import { formatPLN } from '@/utils/currency';
import { formatPct } from '@/components/dashboard/chart-utils';
import { t } from '@/i18n';
import { ProjectDialog, type ProjectFormValues } from './ProjectDialog';
import { suggestColor } from '@/utils/suggestColor';
import { profitClass } from './project-utils';

interface ProjectsViewProps {
  companyNip: string;
  companyName: string;
  onProjectSelect: (projectId: string) => void;
}

const COLUMN_COUNT = 8;

function StatusBadge({ status }: { status: ProjectListItem['status'] }) {
  return status === 'active' ? (
    <Badge
      variant="outline"
      className="border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
    >
      {t('projects.status.active')}
    </Badge>
  ) : (
    <Badge variant="outline" className="text-muted-foreground">
      {t('projects.status.closed')}
    </Badge>
  );
}

/**
 * Task B3: projects list with computed P&L. Mutations (create/edit/close) are
 * admin/ksiegowy-only — the backend enforces this; the UI hides the buttons
 * for everyone else (same convention as payment/note management).
 */
export function ProjectsView({ companyNip, onProjectSelect }: ProjectsViewProps) {
  const [projects, setProjects] = useState<ProjectListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Project | null>(null);
  const [saving, setSaving] = useState(false);
  const [closeTarget, setCloseTarget] = useState<ProjectListItem | null>(null);

  // Default for the create form: hue farthest from the colors already used.
  const suggestedColor = useMemo(
    () => suggestColor(projects.map((p) => p.color)),
    [projects],
  );
  // Retroactive rule application: dry-run counts awaiting the user's confirm.
  const [applyTarget, setApplyTarget] = useState<{
    projectId: string;
    result: ProjectTagRuleApplyResult;
  } | null>(null);

  const { listProjects, getProject, createProject, updateProject, setTagRules, applyTagRules } =
    useProjectsApi();
  const { isSuperAdmin, permissions } = useAuth();
  const canManage = isSuperAdmin || hasCompanyRole(permissions, companyNip, ['admin', 'ksiegowy']);

  const loadProjects = useCallback(async () => {
    if (!companyNip) return;
    try {
      setLoading(true);
      const data = await listProjects(companyNip);
      setProjects(data);
    } catch (error) {
      toast.error(t('projects.loadError'));
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [companyNip, listProjects]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  // The list row lacks description — fetch the full record so the edit form
  // is complete. Falls back to opening with what we have on failure.
  const openEdit = async (row: ProjectListItem) => {
    try {
      const project = await getProject(companyNip, row.id);
      setEditing(project);
      setDialogOpen(true);
    } catch (error) {
      toast.error(t('projects.loadError'));
      console.error(error);
    }
  };

  /**
   * Tag-rules part of the edit save. Runs AFTER the project fields saved;
   * failures here must not mask that success, so they toast separately.
   * When tags were ADDED, a dryRun=true preview decides whether to open the
   * retroactive-apply confirmation (nothing is written without the confirm).
   */
  const saveTagRules = async (
    projectId: string,
    tagRules: NonNullable<ProjectFormValues['tagRules']>,
  ) => {
    const { tagIds, initialTagIds } = tagRules;
    const changed =
      tagIds.length !== initialTagIds.length || tagIds.some((id) => !initialTagIds.includes(id));
    if (!changed) return;
    try {
      await setTagRules(companyNip, projectId, tagIds);
      const added = tagIds.filter((id) => !initialTagIds.includes(id));
      if (added.length === 0) return;
      const dry = await applyTagRules(companyNip, projectId, true);
      if (dry.matched > 0) {
        setApplyTarget({ projectId, result: dry });
      } else {
        toast.info(t('projects.tagRules.noMatches'));
      }
    } catch (error) {
      toast.error(t('projects.tagRules.saveError'));
      console.error(error);
    }
  };

  const handleSubmit = async (values: ProjectFormValues) => {
    try {
      setSaving(true);
      if (editing) {
        const dto: UpdateProjectDto = {
          name: values.name,
          code: values.code || null,
          description: values.description || null,
          color: values.color || null,
        };
        await updateProject(companyNip, editing.id, dto);
        // tagRules === null means "never loaded" — leave the rules untouched.
        if (values.tagRules) {
          await saveTagRules(editing.id, values.tagRules);
        }
        toast.success(t('projects.updateSuccess'));
      } else {
        await createProject(companyNip, {
          name: values.name,
          code: values.code || undefined,
          description: values.description || undefined,
          color: values.color || undefined,
        });
        toast.success(t('projects.createSuccess'));
      }
      setDialogOpen(false);
      setEditing(null);
      await loadProjects();
    } catch (error) {
      toast.error(editing ? t('projects.updateError') : t('projects.createError'));
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  // The confirmed retroactive apply — the ONLY dryRun=false call.
  const handleConfirmApply = async (target: { projectId: string }) => {
    try {
      const result = await applyTagRules(companyNip, target.projectId, false);
      toast.success(
        t('projects.tagRules.applySuccess', {
          assigned: result.assigned,
          conflicts: result.conflicts,
        }),
      );
      await loadProjects();
    } catch (error) {
      toast.error(t('projects.tagRules.applyError'));
      console.error(error);
    }
  };

  const handleStatusChange = async (row: ProjectListItem, status: 'active' | 'closed') => {
    try {
      await updateProject(companyNip, row.id, { status });
      toast.success(status === 'closed' ? t('projects.closeSuccess') : t('projects.reopenSuccess'));
      await loadProjects();
    } catch (error) {
      toast.error(t('projects.statusError'));
      console.error(error);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {canManage && (
        <HeaderActions>
          <Button size="sm" onClick={openCreate}>
            <Plus className="size-4" />
            {t('projects.newProject')}
          </Button>
        </HeaderActions>
      )}

      <div className="bg-card rounded-xl border">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="text-muted-foreground">{t('projects.columns.name')}</TableHead>
                <TableHead className="text-muted-foreground w-28">{t('projects.columns.status')}</TableHead>
                <TableHead className="text-muted-foreground w-32 text-right">{t('projects.columns.cost')}</TableHead>
                <TableHead className="text-muted-foreground w-32 text-right">{t('projects.columns.revenue')}</TableHead>
                <TableHead className="text-muted-foreground w-32 text-right">{t('projects.columns.profit')}</TableHead>
                <TableHead className="text-muted-foreground w-24 text-right">{t('projects.columns.margin')}</TableHead>
                <TableHead className="text-muted-foreground w-20 text-right">{t('projects.columns.invoiceCount')}</TableHead>
                <TableHead className="text-muted-foreground w-14">
                  {canManage ? t('projects.columns.actions') : null}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }, (_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={COLUMN_COUNT}>
                      <Skeleton className="h-8 w-full" />
                    </TableCell>
                  </TableRow>
                ))
              ) : projects.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT} className="h-40 text-center">
                    <FolderKanban className="text-muted-foreground mx-auto mb-2 size-8 opacity-50" />
                    <p className="font-medium">{t('projects.noProjects')}</p>
                    <p className="text-muted-foreground text-sm">{t('projects.noProjectsDescription')}</p>
                  </TableCell>
                </TableRow>
              ) : (
                projects.map((project) => (
                  <TableRow
                    key={project.id}
                    className="cursor-pointer"
                    onClick={() => onProjectSelect(project.id)}
                  >
                    <TableCell className="py-2">
                      <div className="flex items-center gap-2">
                        <span
                          className="inline-block size-2.5 shrink-0 rounded-full border"
                          style={{ backgroundColor: project.color ?? 'transparent' }}
                          aria-hidden="true"
                        />
                        <span className="max-w-72 truncate text-sm font-medium" title={project.name}>
                          {project.name}
                        </span>
                        {project.code && (
                          <span className="text-muted-foreground font-mono text-xs">{project.code}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="py-2">
                      <StatusBadge status={project.status} />
                    </TableCell>
                    <TableCell className="py-2 text-right font-mono text-sm tabular-nums">
                      {formatPLN(project.costNet)}
                    </TableCell>
                    <TableCell className="py-2 text-right font-mono text-sm tabular-nums">
                      {formatPLN(project.revenueNet)}
                    </TableCell>
                    <TableCell
                      className={`py-2 text-right font-mono text-sm font-semibold tabular-nums ${profitClass(project.profit)}`}
                    >
                      {formatPLN(project.profit)}
                    </TableCell>
                    <TableCell className="py-2 text-right font-mono text-sm tabular-nums">
                      {project.marginPct !== null ? `${formatPct(project.marginPct)}%` : '—'}
                    </TableCell>
                    <TableCell className="py-2 text-right text-sm tabular-nums">
                      {project.invoiceCount}
                    </TableCell>
                    <TableCell className="py-2" onClick={(e) => e.stopPropagation()}>
                      {canManage && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="size-7">
                              <MoreHorizontal className="size-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEdit(project)}>
                              {t('projects.actions.edit')}
                            </DropdownMenuItem>
                            {project.status === 'active' ? (
                              <DropdownMenuItem onClick={() => setCloseTarget(project)}>
                                {t('projects.actions.close')}
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem onClick={() => handleStatusChange(project, 'active')}>
                                {t('projects.actions.reopen')}
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <ProjectDialog
        open={dialogOpen}
        suggestedColor={suggestedColor}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) setEditing(null);
        }}
        companyNip={companyNip}
        editing={editing}
        saving={saving}
        onSubmit={handleSubmit}
      />

      {/* Retroactive-apply confirmation: numbers come from the dry run; only
          confirming here triggers the writing dryRun=false call. */}
      <AlertDialog open={applyTarget !== null} onOpenChange={(open) => !open && setApplyTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('projects.tagRules.applyConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('projects.tagRules.applyConfirmMessage', {
                matched: applyTarget?.result.matched ?? 0,
                assigned: applyTarget?.result.assigned ?? 0,
                conflicts: applyTarget?.result.conflicts ?? 0,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              disabled={(applyTarget?.result.assigned ?? 0) === 0}
              onClick={() => {
                if (applyTarget) handleConfirmApply(applyTarget);
                setApplyTarget(null);
              }}
            >
              {t('projects.tagRules.applyConfirmAction')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={closeTarget !== null} onOpenChange={(open) => !open && setCloseTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('projects.closeConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('projects.closeConfirmMessage', { name: closeTarget?.name ?? '' })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (closeTarget) handleStatusChange(closeTarget, 'closed');
                setCloseTarget(null);
              }}
            >
              {t('projects.actions.close')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
